import { t } from 'i18n';
import React, { ReactElement, useState } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal,
} from '@teko/ui';
import { ListTile } from '@teko/ecommerce';
import { useAuth } from '@teko/auth-react';
import { getTrackingData } from '@teko/tracking';
import { NavigationButton } from './style';
import { styled, CustomLink, Button, Body } from '@teko/ds-desktop';
import { AccountOutlined } from '@teko/ds-icon';
import { onSignOut } from 'helpers';
import { useServices, useTenantConfig } from 'contexts';

export interface UserProfileHeaderProps {
  vertical?: boolean;
  customProfile?: React.ReactNode;
  profileLinks?: ProfileLinkProps[];
}
const Avatar = styled.div`
  width: 36px;
  height: 36px;
  margin: 5px 0 4px 0;
  &.vertical-avatar {
    width: 30px;
    height: 30px;
    margin: 3px;
  }
`;
const AvatarImage = styled.img`
  width: 100%;
  border-radius: 50%;
`;
const StyledDropdown = styled(Dropdown)`
  width: auto;
  z-index: 9;
`;
const DropdownContent = styled.div`
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  width: 250px;
`;
const BriefUserPanel = styled.div`
  padding: 0.5rem;
  display: flex;
`;
const UserAvatar = styled.div`
  width: 44px;
  margin-right: 0.5rem;
  img {
    width: 100%;
  }
`;
const UserInfo = styled.div`
  .name {
    font-size: 1rem;
    margin-bottom: 0;
  }
  .extra {
    font-size: 0.9rem;
    font-weight: 400;
  }
`;
const StyledButton = styled(Button)`
  height: 2rem;
  width: 100%;
`;
const ButtonWrapper = styled.div`
  padding: 10px;
`;
const ModalAction = styled.div`
  display: flex;
  justify-content: space-between;
`;
const AcceptButton = styled(Button)`
  min-width: 100px;
  height: 36px;
  padding: 0 20px;
  margin-left: 8px;
`;
const DeclineButton = styled(Button)`
  min-width: 100px;
  height: 36px;
  padding: 0 20px;
  margin-right: 8px;
`;
const SigningButton = styled.div`
  min-width: 90px;
  padding: 0.365rem 0.75rem;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  :before {
    content: ' ';
    width: 30px;
    height: 30px;
    border-radius: 15px;
    background: rgb(236, 236, 236);
    background: linear-gradient(
      90deg,
      rgba(236, 236, 236, 1) 0%,
      rgba(227, 227, 227, 1) 100%
    );
  }
  .title {
    width: 60px;
    height: 8px;
    margin-top: 9px;
    border-radius: 4px;
    background: rgb(236, 236, 236);
    background: linear-gradient(
      90deg,
      rgba(236, 236, 236, 1) 0%,
      rgba(227, 227, 227, 1) 100%
    );
  }
`;
interface ProfileLinkProps {
  icon: ReactElement;
  href: string;
  regionName: string;
  contentName: string;
  text: string;
  onClick?: () => void;
}
const ProfileLink: React.FC<ProfileLinkProps> = ({
  icon,
  text,
  href,
  regionName,
  contentName,
  onClick,
}: ProfileLinkProps) => {
  return (
    <CustomLink href={href}>
      <ListTile
        leading={icon}
        title={text}
        numberOfLine={2}
        onClick={onClick}
        {...getTrackingData({
          isTrackContent: true,
          regionName,
          contentName,
        })}
      />
    </CustomLink>
  );
};
export const UserProfileHeader: React.FunctionComponent<
  UserProfileHeaderProps
> = ({
  vertical = false,
  customProfile,
  profileLinks,
}: UserProfileHeaderProps) => {
  if (customProfile) return <>{customProfile}</>;
  const { user: userProfile, loading: profileLoading, authService } = useAuth();
  const { notificationAppId } = useTenantConfig();
  const { notificationService } = useServices();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);

  const toggle = (): void => setDropdownOpen((prevState) => !prevState);
  const onLogin = () => authService.login();
  if (profileLoading) {
    return (
      <SigningButton className="button">
        <span className="title" />
      </SigningButton>
    );
  }
  if (!userProfile?.userId) {
    return vertical ? (
      <NavigationButton
        className="button"
        onClick={onLogin}
        style={{ flexDirection: 'column' }}
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'loginIcon',
        })}
      >
        <AccountOutlined size={36} color="textSecondary" />
        <Body level={2} weight="medium" color="textSecondary" className="title">
          {t('Đăng nhập')}
        </Body>
      </NavigationButton>
    ) : (
      <NavigationButton
        onClick={onLogin}
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'loginIcon',
        })}
      >
        <AccountOutlined size={36} color="textSecondary" />
        <div className="text-left ml-6">
          <Body
            level={2}
            weight="medium"
            color="textSecondary"
            className="title"
          >
            {t('Đăng nhập')}
          </Body>
          <Body
            level={2}
            weight="medium"
            color="textSecondary"
            className="title"
          >
            {t('Đăng ký')}
          </Body>
        </div>
      </NavigationButton>
    );
  }
  return (
    <>
      <Modal
        show={showLogoutConfirmModal}
        size="sm"
        onHide={(): void => setShowLogoutConfirmModal(false)}
      >
        <ModalHeader>{t('Đăng xuất')}</ModalHeader>
        <ModalBody>
          <div>{t('Bạn có chắc chắn muốn đăng xuất không?')}</div>
        </ModalBody>
        <ModalFooter>
          <ModalAction>
            <DeclineButton
              variant="ghost"
              onClick={(): void => setShowLogoutConfirmModal(false)}
            >
              {t('Không')}
            </DeclineButton>
            <AcceptButton
              variant="primary"
              onClick={(): void => {
                onSignOut(
                  authService,
                  notificationService,
                  notificationAppId,
                  window.location.origin,
                );
                setShowLogoutConfirmModal(false);
              }}
            >
              {t('Có')}
            </AcceptButton>
          </ModalAction>
        </ModalFooter>
      </Modal>
      <StyledDropdown
        isOpen={dropdownOpen}
        toggle={toggle}
        toggleType="hover"
        onHide={(): void => setDropdownOpen(false)}
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'profileIcon',
        })}
      >
        <DropdownToggle>
          <NavigationButton
            className={vertical ? 'button' : ''}
            style={
              vertical ? { flexDirection: 'column' } : { flexDirection: 'row' }
            }
          >
            {userProfile.avatar ? (
              <Avatar className={vertical ? 'vertical-avatar' : ''}>
                <AvatarImage
                  src={userProfile.avatar}
                  aria-label="Current User Avatar"
                />
              </Avatar>
            ) : (
              <AccountOutlined size={36} color="textSecondary" />
            )}
            {vertical ? (
              <Body
                level={2}
                weight="medium"
                color="textSecondary"
                className="title"
              >
                {userProfile.name || userProfile.telephone}
              </Body>
            ) : (
              <div className="text-left ml-12">
                <Body
                  level={2}
                  weight="medium"
                  color="textSecondary"
                  className="title"
                >
                  {t('Xin chào,')}
                </Body>
                <Body
                  level={2}
                  weight="medium"
                  color="textSecondary"
                  className="title"
                >
                  {userProfile.name || userProfile.telephone}
                </Body>
              </div>
            )}
          </NavigationButton>
        </DropdownToggle>
        <DropdownMenu placement="bottom">
          <DropdownContent>
            <BriefUserPanel>
              <UserAvatar>
                {userProfile.avatar ? (
                  <img src={userProfile.avatar} alt="Avatar" />
                ) : (
                  <AccountOutlined size={36} color="textSecondary" />
                )}
              </UserAvatar>
              <UserInfo>
                <h3 className="name">{userProfile.name}</h3>
                <h5 className="extra">
                  {userProfile.email || userProfile.telephone}
                </h5>
              </UserInfo>
            </BriefUserPanel>
            {profileLinks &&
              profileLinks.map((profileLink: ProfileLinkProps) => (
                <ProfileLink
                  key={profileLink.href}
                  icon={profileLink.icon}
                  href={profileLink.href}
                  text={profileLink.text}
                  contentName={profileLink.contentName}
                  regionName={profileLink.regionName}
                  onClick={() => setDropdownOpen(false)}
                />
              ))}
            <ButtonWrapper>
              <StyledButton
                onClick={() => {
                  setDropdownOpen(false);
                  setShowLogoutConfirmModal(true);
                }}
                variant="primary"
                {...getTrackingData({
                  isTrackContent: true,
                  regionName: 'headerBar',
                  contentName: 'logOut',
                })}
              >
                {t('Đăng xuất')}
              </StyledButton>
            </ButtonWrapper>
          </DropdownContent>
        </DropdownMenu>
      </StyledDropdown>
    </>
  );
};
